import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error('Missing reset token');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('Password must be at least 8 characters');
    if (password !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="mb-8 text-center">
          <img src={LOGO_URL} alt="Kinsy" className="h-10 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-stone-900 tracking-tight mb-2">Choose a new password</h1>
          <p className="text-stone-500">Pick something at least 8 characters long.</p>
        </div>

        <div className="card-soft">
          {done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-teal-600" />
              </div>
              <h2 className="text-lg font-semibold text-stone-900 mb-2">Password reset</h2>
              <p className="text-stone-600 text-sm mb-6">Redirecting to sign in…</p>
              <Button asChild className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full">
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-stone-700 flex items-center gap-2">
                  <Lock className="w-4 h-4" /> New password
                </Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                    data-testid="new-password-input"
                    className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500 pr-10"
                  />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-stone-700">Confirm password</Label>
                <Input
                  id="confirm-password"
                  type={show ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                  className="h-12 rounded-xl border-stone-200 focus:border-teal-500 focus:ring-teal-500"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !token}
                className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-medium"
                data-testid="reset-submit-btn"
              >
                {loading ? 'Resetting…' : 'Reset password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
